import { useCallback, useEffect, useMemo } from 'react';
import { useFetcher } from '@remix-run/react';
import type { UpdateInspirationItemMutation } from 'generated/graphql';
import { UPDATE_INSPIRATION_ITEM_API_ROUTE } from '~/routes/remix-api.build-hub.inspiration-item-update';

interface UseBuildHubInspirationItemUpdateOptions {
    onSuccess?: () => void;
    onError?: (error: string) => void;
}

interface UseBuildHubInspirationItemUpdateReturn {
    updateInspirationItem: (params: {
        inspirationItemId: string;
        title?: string;
        description?: string;
    }) => void;
    isUpdating: boolean;
    error: string | null;
    isSuccess: boolean;
    isIdle: boolean;
}

/**
 * Hook for updating InspirationItem title and description
 *
 * Handles the GraphQL mutation via Remix fetcher, loading states, error handling, and success callbacks.
 *
 * @example
 * const { updateInspirationItem, isUpdating, error } = useBuildHubInspirationItemUpdate({
 *   onSuccess: () => toast.success('Updated!'),
 *   onError: (err) => toast.error(err),
 * });
 *
 * // Usage
 * updateInspirationItem({
 *   inspirationItemId: '123',
 *   title: 'New Title',
 * });
 */
export function useBuildHubInspirationItemUpdate(
    options: UseBuildHubInspirationItemUpdateOptions = {}
): UseBuildHubInspirationItemUpdateReturn {
    const { onSuccess, onError } = options;
    const updateFetcher = useFetcher<UpdateInspirationItemMutation>();

    // Derived states
    const data = updateFetcher.data?.projectInspirationItemUpdate;
    const isUpdating = updateFetcher.state !== 'idle';
    const isIdle = !isUpdating;
    const isSuccess = Boolean(
        isIdle && data && data.__typename === 'InspirationItem'
    );

    /**
     * Submit update request
     */
    const updateInspirationItem = useCallback(
        ({
            inspirationItemId,
            title,
            description,
        }: {
            inspirationItemId: string;
            title?: string;
            description?: string;
        }) => {
            const formData = new FormData();
            formData.append('inspirationItemId', inspirationItemId);
            if (title !== undefined) {
                formData.append('title', title);
            }
            if (description !== undefined) {
                formData.append('description', description);
            }

            updateFetcher.submit(formData, {
                method: 'post',
                action: UPDATE_INSPIRATION_ITEM_API_ROUTE,
            });
        },
        [updateFetcher]
    );

    /**
     * Extract error message from response
     */
    const error = useMemo(() => {
        if (isIdle && data && data.__typename !== 'InspirationItem') {
            const errorMessage =
                'Message' in data
                    ? data.Message
                    : 'Failed to update inspiration item';
            return errorMessage;
        }
        return null;
    }, [isIdle, data]);

    /**
     * Handle successful update response
     */
    useEffect(() => {
        if (isSuccess) {
            onSuccess?.();
        }
    }, [isSuccess, onSuccess]);

    /**
     * Handle error response
     */
    useEffect(() => {
        if (error) {
            onError?.(error);
        }
    }, [error, onError]);

    return {
        updateInspirationItem,
        isUpdating,
        error,
        isSuccess,
        isIdle,
    };
}
